import React, { useEffect, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * SentenceAnatomy — "The Two Building Blocks" tappable sentence gallery.
 *
 * Sentences render whole; tapping one splits it into its color-coded مبتدأ
 * (gold) and خبر (violet) with labels, gloss, and audio. Completes when every
 * sentence has been opened.
 */
export default function SentenceAnatomy({ data, onComplete, audio }) {
  const [openedIds, setOpenedIds] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState(null);

  const sentences = data.sentences;
  const labels = data.part_labels;
  const allOpened = openedIds.size >= sentences.length;

  useEffect(() => {
    if (allOpened) onComplete();
  }, [allOpened, onComplete]);

  const handleOpen = (sentence) => {
    setOpenedIds((prev) => new Set(prev).add(sentence.id));
    setExpandedId((id) => (id === sentence.id ? null : sentence.id));
    audio.play(`${sentence.mubtada_ar} ${sentence.khabar_ar}`);
  };

  return (
    <ActivityCard>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div className="flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--gold)" }} aria-hidden="true" />
            <span dir="rtl" lang="ar" className="font-arabic text-lg" style={{ color: "var(--gold-bright)" }}>{labels.mubtada.arabic}</span>
            <span style={{ color: "var(--parchment-dim)" }}>{labels.mubtada.en}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--tag-violet)" }} aria-hidden="true" />
            <span dir="rtl" lang="ar" className="font-arabic text-lg" style={{ color: "var(--tag-violet)" }}>{labels.khabar.arabic}</span>
            <span style={{ color: "var(--parchment-dim)" }}>{labels.khabar.en}</span>
          </span>
        </div>
        <ProgressPill>{openedIds.size}/{sentences.length} opened</ProgressPill>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {sentences.map((sentence) => {
          const opened = openedIds.has(sentence.id);
          const expanded = expandedId === sentence.id;
          return (
            <button
              key={sentence.id}
              type="button"
              onClick={() => handleOpen(sentence)}
              aria-expanded={expanded}
              className="text-left rounded-xl px-4 py-3 transition-all hover:brightness-110 w-full"
              style={{
                background: "var(--ink)",
                border: expanded ? "1px solid var(--tag-violet)" : "1px solid var(--panel-line)",
                boxShadow: expanded ? "0 0 0 3px var(--violet-bg)" : "none",
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.8]">
                  {expanded ? (
                    <>
                      <span
                        className="rounded-md px-1"
                        style={{ color: "var(--gold-bright)", background: "var(--gold-bg)" }}
                      >
                        {sentence.mubtada_ar}
                      </span>{" "}
                      <span
                        className="rounded-md px-1"
                        style={{ color: "var(--tag-violet)", background: "var(--violet-bg)" }}
                      >
                        {sentence.khabar_ar}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: opened ? "var(--tag-violet)" : "var(--parchment)" }}>
                      {sentence.mubtada_ar} {sentence.khabar_ar}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {opened && (
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ color: "var(--teal)" }}>
                      <path d="M5 10.5l3.2 3.2L15 7" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {audio.audioAllowed && expanded && (
                    <PlayButton
                      text={`${sentence.mubtada_ar} ${sentence.khabar_ar}`}
                      playingText={audio.playingText}
                      onPlay={audio.play}
                    />
                  )}
                </span>
              </div>

              {expanded ? (
                <div className="mt-2">
                  <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>{sentence.en}</p>
                  <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
                    <span style={{ color: "var(--gold-bright)" }}>{labels.mubtada.arabic}</span> {labels.mubtada.hint} ·{" "}
                    <span style={{ color: "var(--tag-violet)" }}>{labels.khabar.arabic}</span> {labels.khabar.hint}
                  </p>
                  {sentence.note && (
                    <p className="text-xs mt-1 mb-0 italic" style={{ color: "var(--tag-violet)" }}>{sentence.note}</p>
                  )}
                </div>
              ) : (
                <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
                  {opened ? sentence.en : "Tap to split"}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </ActivityCard>
  );
}
